import {
    faAngleDown,
    faAngleRight,
    faPen,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import {
    faPen as faPenSolid,
    faTrash as faTrashSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import {
    BoolFieldElement,
    DoubleValidator,
    InputMode,
    IntValidator,
    StringValidator,
} from '@kcftech/prometheustypescriptapiclient';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { Detail } from 'components/detail/detail';
import {
    AssemblyParameterElementDto,
    getParameterType,
} from 'pages/Settings/AssemblyType/parameter_form/types';
import { useState } from 'react';
import { DebugValue } from 'shared/debug/DebugValue';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { formattedParameterTypes } from 'utils/assembly_application';

const ParameterContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const Header = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const ParameterInfo = styled.div`
    display: flex;
    flex-direction: column;
    padding-left: 2rem;
`;

const ExpandButton = styled(Button)`
    margin-right: 0.5rem;
    width: 1.5rem;
`;

export type AssemblyParameterDisplayProps = Readonly<{
    parameter: AssemblyParameterElementDto;
    isExpanded: boolean;
    onExpand: () => void;
    onEdit: () => void;
    onDelete: () => void;
}>;

const formatNumber = (value: number, isInclusive: boolean): string =>
    isInclusive ? `${value} (inclusive)` : `${value}`;

export function AssemblyParameterDisplay({
    parameter,
    isExpanded,
    onExpand,
    onEdit,
    onDelete,
}: AssemblyParameterDisplayProps) {
    const [optionsExpanded, setOptionsExpanded] = useState(false);

    const parameterType = formattedParameterTypes[getParameterType(parameter)];

    return (
        <ParameterContainer>
            <Header>
                <div className="flex-row">
                    <ExpandButton
                        spacing="none"
                        appearance="subtle"
                        onClick={() => onExpand()}
                    >
                        <FontAwesomeIcon icon={isExpanded ? faAngleDown : faAngleRight} />
                    </ExpandButton>
                    <div>{parameter.FieldDisplayName}</div>
                    <DebugValue
                        value={parameter}
                        description="Assembly Application Parameter"
                    />
                </div>
                <div className="flex-row">
                    <HoverIconButton
                        title={`Edit ${parameter.FieldDisplayName}`}
                        ariaLabel={`edit ${parameter.FieldDisplayName} parameter button`}
                        icon={faPen}
                        hoverIcon={faPenSolid}
                        color={tokens.icon.actionActive}
                        hoverColor={tokens.icon.actionHover}
                        size="14"
                        onClick={() => onEdit()}
                    />
                    <HoverIconButton
                        title={`Delete ${parameter.FieldDisplayName}`}
                        ariaLabel={`delete ${parameter.FieldDisplayName} parameter button`}
                        icon={faTrash}
                        hoverIcon={faTrashSolid}
                        color={tokens.icon.actionActive}
                        hoverColor={tokens.error.default}
                        size="14"
                        onClick={() => onDelete()}
                    />
                </div>
            </Header>
            {isExpanded && (
                <ParameterInfo>
                    <Detail name="Code">{parameter.FieldKey}</Detail>
                    <Detail name="Type">{parameterType}</Detail>
                    {parameter.Validator &&
                        (parameter.Validator instanceof IntValidator ||
                            parameter.Validator instanceof DoubleValidator) && (
                            <>
                                {parameter.Validator.Min && (
                                    <Detail name="Min Value">
                                        {formatNumber(
                                            parameter.Validator.Min.Value,
                                            !parameter.Validator.Min.Exclusive
                                        )}
                                    </Detail>
                                )}
                                {parameter.Validator.Max && (
                                    <Detail name="Max Value">
                                        {formatNumber(
                                            parameter.Validator.Max.Value,
                                            !parameter.Validator.Max.Exclusive
                                        )}
                                    </Detail>
                                )}
                            </>
                        )}
                    {parameter.InputMode === InputMode.Select &&
                        parameter.Validator &&
                        parameter.Validator instanceof StringValidator &&
                        parameter.Validator.ValidValues &&
                        parameter.Validator.ValidValues.length > 0 && (
                            <div className="pb-10">
                                <Header>
                                    <div className="flex-row">
                                        <ExpandButton
                                            spacing="none"
                                            appearance="subtle"
                                            onClick={() =>
                                                setOptionsExpanded(!optionsExpanded)
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    optionsExpanded
                                                        ? faAngleDown
                                                        : faAngleRight
                                                }
                                            />
                                        </ExpandButton>
                                        <div className="f-14">Options</div>
                                    </div>
                                </Header>
                                <ParameterInfo>
                                    {optionsExpanded &&
                                        parameter.Validator.ValidValues.map((value) => (
                                            <div key={value}>{value}</div>
                                        ))}
                                </ParameterInfo>
                            </div>
                        )}
                    {parameter.DefaultValue !== undefined &&
                        parameter.DefaultValue !== '' && (
                            <Detail name="Default Value">
                                {parameter instanceof BoolFieldElement ? (
                                    <>{parameter.DefaultValue === true ? 'Yes' : 'No'}</>
                                ) : (
                                    parameter.DefaultValue
                                )}
                            </Detail>
                        )}
                </ParameterInfo>
            )}
        </ParameterContainer>
    );
}
